import { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { usePageTitle } from '../../../hooks/usePageTitle'
import { Field } from '../../../models/Field'
import { IconNameAndCategoryString } from '../../Icon'
import Button from '../../app/design/Button'
import Heading from '../../app/design/Heading'
import Paragraph from '../../app/design/Paragraph'
import { ConfirmationModal } from '../../common/modals/ConfirmationModal'
import { WorkflowPage } from '../../workflows/WorkflowPage'
import { useWorkflowPageScrollReset } from '../../workflows/useNavigationHandlers'
import { NPSContext, useNPSContext } from './NPSViewModel'
import { NPSWorkflowState } from './useNPSFormWorkflowCoordinator'

export type FieldSpec = {
    id: string
    /**
     * Explicit label override for default field label.
     */
    label?: string
    sublabel?: string
    icon?: IconNameAndCategoryString
    statusLine?: (value: unknown, field: Field) => string
    render: (value: unknown, onChange: (value: unknown) => void, field: Field) => ReactNode
}

export type NpsStepProps = {
    title?: string
    subtitle?: string
    fieldGroups: FieldSpec[][]
}

export function NpsStep({ title, subtitle, fieldGroups }: NpsStepProps) {
    const { setField, getField, getFieldValue } = useNPSContext()

    return (
        <div className="pt-8">
            {title && (
                <Heading className="mx-auto w-145 text-center" size="xl">
                    {title}
                </Heading>
            )}
            {subtitle && (
                <Paragraph className="mx-auto mt-4 w-145 text-center text-gray-600" size="md">
                    {subtitle}
                </Paragraph>
            )}
            {fieldGroups.map((group) => (
                <WorkflowPage.Card
                    key={group.map(({ id }) => id).join('-')}
                    className="mt-12 gap-y-8"
                >
                    {group.map(
                        ({ id, icon, render, label, sublabel, statusLine: getStatusLine }) => {
                            const field = getField(id)

                            // Should never happen when used with valid ids.
                            if (!field) {
                                throw new Error(`Field with id '${id}' not found.`)
                            }

                            const value = getFieldValue(id)
                            const title = label ?? field.label!
                            const statusLine = getStatusLine?.(value, field)

                            let section
                            if (icon) {
                                section = (
                                    <WorkflowPage.IconSection
                                        key={id}
                                        title={title}
                                        description={sublabel}
                                        icon={icon}
                                        secondaryText={statusLine}
                                        secondaryTextClassName="whitespace-nowrap"
                                    >
                                        {render(
                                            value,
                                            (value: unknown) => setField(id, value),
                                            field
                                        )}
                                    </WorkflowPage.IconSection>
                                )
                            }
                            else {
                                section = (
                                    <div key={id}>
                                        <Heading size="sm" className="mb-8 block">
                                            {title}
                                        </Heading>
                                        <Paragraph
                                            className="mb-6 font-normal text-gray-600"
                                            size="md"
                                        >
                                            {sublabel}
                                        </Paragraph>
                                        <div>
                                            {render(
                                                value,
                                                (value: unknown) => setField(id, value),
                                                field
                                            )}
                                        </div>
                                    </div>
                                )
                            }

                            return section
                        }
                    )}
                </WorkflowPage.Card>
            ))}
        </div>
    )
}

export type NpsStepBasePageProps<StepId extends string> = {
    title: string
    workflow: NPSWorkflowState<StepId>
    children: ReactNode
}

export function NPSStepBasePage<StepId extends string>({
    title,
    workflow: {
        next,
        close,
        cancelClose,
        isNextDisabled,
        isSaving,
        isTransitioning,
        currentStepIndex,
        currentStep: { shouldShowFooter = true, isCentered = false },
        isCloseConfirmationActive,
        recordForm,
    },
    children,
}: NpsStepBasePageProps<StepId>) {
    useWorkflowPageScrollReset([currentStepIndex])
    usePageTitle(title)

    let footer
    if (shouldShowFooter) {
        footer = (
            <div className="flex justify-end gap-x-4">
                <Button
                    intent="primary"
                    type="submit"
                    isLoading={isSaving}
                    disabled={isNextDisabled || isTransitioning}
                    onClick={next}
                >
                    Next
                </Button>
            </div>
        )
    }

    return (
        <NPSContext.Provider value={recordForm}>
            <WorkflowPage
                footerContent={footer}
                onClose={close}
                shouldVerticallyCenterContent={isCentered}
                isCloseDisabled={isTransitioning}
            >
                <div
                    className={twMerge(
                        'mx-auto flex flex-col items-stretch justify-start',
                        isCentered ? 'w-medium' : 'w-large'
                    )}
                >
                    {children}
                </div>
            </WorkflowPage>
            <ConfirmationModal
                title="Exit survey?"
                isOpen={isCloseConfirmationActive}
                onConfirm={close}
                onClose={cancelClose}
                onCancel={cancelClose}
            >
                If you exit without completing the survey, you will not receive your free Smart
                Campaign credits.
            </ConfirmationModal>
        </NPSContext.Provider>
    )
}
